import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {MapboxControlGroup} from '@gaia/utils/mapbox/MapboxControl';
import SubscriptionTooltip from '@map/SubscriptionTooltip';
import {Slider} from '@mui/material';
import debounce from 'lodash/debounce';
import type mapboxgl from 'mapbox-gl';
import {useCallback, useEffect, useState} from 'react';

const sliderStyle = {
    '& .MuiSlider-thumb': {
        '&:focus, &:hover, &:active': {
            boxShadow: 'none',
        },
    },
};

export default function PitchControl({map}: {map: mapboxgl.Map | undefined}) {
    const [pitch, setPitch] = useState(30);

    const onPitch = useCallback(() => {
        if (!map) return;
        setPitch(Math.round(map.getPitch()));
    }, [map]);

    useEffect(() => {
        if (!map) return;
        map.on('pitch', onPitch);
        // If the user flicks the mouse while changing the pitch, the 'pitch' event
        // will not fire when the movement ends, thus a handler on pitchend is needed
        map.on('pitchend', onPitch);

        return () => {
            map.off('pitch', onPitch);
            map.off('pitchend', onPitch);
        };
    }, [map, onPitch]);

    const debouncedItly = debounce(() => ampli.changePitch({category: 'map2'}), 2000, {
        leading: false,
        trailing: true,
    });

    // Custom management of the tooltip state ensures that the tooltip does not display
    // while the slider is active
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const onPitchChange = useCallback(
        (event: Event, value: number | number[]) => {
            setTooltipOpen(false);
            debouncedItly();
            map?.setPitch(Math.round(value as number));
            setPitch(Math.round(value as number));
        },
        [debouncedItly, map, setPitch]
    );

    const onMouseEnter = useCallback(() => {
        setTooltipOpen(true);
    }, [setTooltipOpen]);
    const onMouseLeave = useCallback(() => {
        setTooltipOpen(false);
    }, [setTooltipOpen]);

    return (
        <MapboxControlGroup
            style={{
                height: 90,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <SubscriptionTooltip
                enterDelay={500}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                open={tooltipOpen}
                placement='right'
                title={
                    <>
                        <span style={{fontWeight: 'bold'}}>Adjust Pitch</span>
                        <br />
                        CTRL + Drag up/down
                    </>
                }
                subscription={undefined}
            >
                <div style={{height: 60}}>
                    <Slider
                        size='small'
                        sx={sliderStyle}
                        orientation='vertical'
                        min={0}
                        max={80}
                        step={1}
                        value={pitch}
                        onChange={onPitchChange}
                    />
                </div>
            </SubscriptionTooltip>
        </MapboxControlGroup>
    );
}
